import React, { useState, useMemo } from "react";
import { ChevronsUpDown, ChevronLeft, ChevronRight, Check, Search, X } from "lucide-react";

// Mock FormatDateTime function
const FormatDateTime = (dateString) => {
  return dateString;
};

// Helper function to normalize side values
const normalizeSide = (side) => {
  // Convert to number if it's a string
  const numSide = typeof side === 'string' ? parseInt(side) : side;
  
  // Normalize: 1 = BUY, anything else (-1, 2, etc.) = SELL
  return numSide === 1 ? 'BUY' : 'SELL';
};

// Helper function to get symbol-specific multiplier and rounding unit
const getSymbolConfig = (symbol) => {
  const upperSymbol = symbol?.toUpperCase();
  
  if (upperSymbol?.includes('BANKNIFTY')) {
    return {
      multiplierField: 'banknifty_multiplier',
      roundingUnit: 35,
      symbolType: 'BANKNIFTY',
      fallbackBaseQty: 35 // Base quantity for BANKNIFTY (gets multiplied by user's multiplier)
    };
  } else if (upperSymbol?.includes('FINNIFTY')) {
    return {
      multiplierField: 'finnifty_multiplier',
      roundingUnit: 65,
      symbolType: 'FINNIFTY',
      fallbackBaseQty: 65 // Base quantity for FINNIFTY
    };
  } else if (upperSymbol?.includes('MIDCPNIFTY')) {
    return {
      multiplierField: 'midcpnifty_multiplier',
      roundingUnit: 20,
      symbolType: 'MIDCPNIFTY',
      fallbackBaseQty: 20 // Base quantity for MIDCPNIFTY
    };
  } else if (upperSymbol?.includes('SENSEX')) {
    return {
      multiplierField: 'sensex_multiplier',
      roundingUnit: 20,
      symbolType: 'SENSEX',
      fallbackBaseQty: 20 // Base quantity for SENSEX
    };
  } else if (upperSymbol?.includes('NIFTY')) {
    return {
      multiplierField: 'index_multiplier',
      roundingUnit: 75,
      symbolType: 'NIFTY',
      fallbackBaseQty: 75 // Base quantity for NIFTY
    };
  }
  
  return null;
};

// Updated function to get base quantity for all index symbols using CA00246 reference
const getIndexBaseQuantity = (symbol, strategy, side, orderData = [], accountNames = []) => {
  const referenceClientId = "CA00246";
  const symbolConfig = getSymbolConfig(symbol);
  
  if (!symbolConfig) {
    console.log(`❌ Symbol "${symbol}" is not recognized as an index symbol`);
    return null;
  }
  
  // Normalize the target side for comparison
  const normalizedTargetSide = normalizeSide(side);
  
  console.log(`\n=== DEBUGGING ${symbolConfig.symbolType} BASE CALCULATION ===`);
  console.log(`🎯 Target: Symbol="${symbol}", Strategy="${strategy}", Side=${side} (normalized: ${normalizedTargetSide}), Client="${referenceClientId}"`);
  console.log(`📊 Symbol Config: Type=${symbolConfig.symbolType}, MultiplierField=${symbolConfig.multiplierField}, RoundingUnit=${symbolConfig.roundingUnit}`);
  
  // Find reference client's multiplier from accountNames
  // console.log(`\n📋 Available Accounts:`);
  accountNames.forEach(u => {
    const multiplier = u[symbolConfig.multiplierField];
    console.log(`  - ID: "${u.client_id}", Name: "${u.name}", ${symbolConfig.symbolType} Multiplier: ${multiplier}`);
  });
  
  const referenceClientData = accountNames.find(user => user.client_id === referenceClientId);
  if (!referenceClientData) {
    console.log(`❌ Reference client "${referenceClientId}" NOT FOUND in accountNames`);
    console.log(`📋 Available client IDs: [${accountNames.map(u => `"${u.client_id}"`).join(', ')}]`);
    
    // FALLBACK: Use symbol's default base quantity
    console.log(`🔄 FALLING BACK TO DEFAULT BASE QUANTITY for ${symbolConfig.symbolType}`);
    const fallbackBaseQty = symbolConfig.fallbackBaseQty;
    console.log(`📊 Using fallback base quantity: ${fallbackBaseQty}`);
    console.log(`🎉 FINAL BASE QUANTITY (FALLBACK): ${fallbackBaseQty}`);
    console.log(`=== END ${symbolConfig.symbolType} DEBUGGING (FALLBACK) ===\n`);
    
    return fallbackBaseQty;
  }
  
  const referenceMultiplier = referenceClientData[symbolConfig.multiplierField];
  if (!referenceMultiplier || referenceMultiplier <= 0) {
    console.log(`❌ Reference client "${referenceClientId}" has invalid ${symbolConfig.symbolType} multiplier: ${referenceMultiplier}`);
    
    // FALLBACK: Use symbol's default base quantity
    console.log(`🔄 FALLING BACK TO DEFAULT BASE QUANTITY for ${symbolConfig.symbolType}`);
    const fallbackBaseQty = symbolConfig.fallbackBaseQty;
    console.log(`📊 Using fallback base quantity: ${fallbackBaseQty}`);
    console.log(`🎉 FINAL BASE QUANTITY (FALLBACK): ${fallbackBaseQty}`);
    console.log(`=== END ${symbolConfig.symbolType} DEBUGGING (FALLBACK) ===\n`);
    
    return fallbackBaseQty;
  }
  
  console.log(`✅ Found reference client: ID="${referenceClientData.client_id}", ${symbolConfig.symbolType} Multiplier=${referenceMultiplier}`);
  
  // Show ALL orders for this symbol
  console.log(`\n📦 All orders for symbol "${symbol}":`);
  const symbolOrders = orderData.filter(o => o.symbol === symbol);
  symbolOrders.forEach((o, i) => {
    console.log(`  [${i}] Strategy="${o.strategy}", Side=${o.side} (normalized: ${normalizeSide(o.side)}), Qty=${o.qty}, ClientIDs=[${o.clientIds.join(', ')}]`);
  });
  
  // Find reference client's order quantity for this EXACT symbol + strategy + normalized side combination
  console.log(`\n🔍 Looking for EXACT match: Symbol="${symbol}" + Strategy="${strategy}" + Side=${normalizedTargetSide} (any raw side value) + Client="${referenceClientId}"`);
  
  const referenceOrder = orderData.find(order => 
    order.symbol === symbol && 
    order.strategy === strategy && 
    normalizeSide(order.side) === normalizedTargetSide && // Use normalized side comparison
    order.clientIds?.includes(referenceClientId)
  );
  
  if (!referenceOrder) {
    console.log(`❌ NO EXACT MATCH FOUND!`);
    console.log(`📦 Orders matching symbol+strategy+normalized side (without client filter):`);
    const partialMatches = orderData.filter(o => 
      o.symbol === symbol && o.strategy === strategy && normalizeSide(o.side) === normalizedTargetSide
    );
    partialMatches.forEach(o => {
      console.log(`  - Qty=${o.qty}, Side=${o.side} (normalized: ${normalizeSide(o.side)}), ClientIDs=[${o.clientIds.join(', ')}], Contains "${referenceClientId}"? ${o.clientIds?.includes(referenceClientId)}`);
    });
    
    // FALLBACK: Use symbol's default base quantity
    console.log(`🔄 FALLING BACK TO DEFAULT BASE QUANTITY for ${symbolConfig.symbolType}`);
    const fallbackBaseQty = symbolConfig.fallbackBaseQty;
    console.log(`📊 Using fallback base quantity: ${fallbackBaseQty}`);
    console.log(`🎉 FINAL BASE QUANTITY (FALLBACK): ${fallbackBaseQty}`);
    console.log(`=== END ${symbolConfig.symbolType} DEBUGGING (FALLBACK) ===\n`);
    
    return fallbackBaseQty;
  }
  
  console.log(`✅ FOUND EXACT MATCH!`);
  console.log(`📦 Order details: Strategy="${referenceOrder.strategy}", Side=${referenceOrder.side} (normalized: ${normalizeSide(referenceOrder.side)}), Qty=${referenceOrder.qty}`);
  console.log(`👥 ClientIDs in this order: [${referenceOrder.clientIds.join(', ')}]`);
  
  const referenceQty = referenceOrder.qty;
  
  // Calculate base quantity using the reference client's actual multiplier
  const rawBaseQty = referenceQty / referenceMultiplier;
  
  // Round the base quantity to nearest multiple of symbol-specific rounding unit
  const baseQty = Math.round(rawBaseQty / symbolConfig.roundingUnit) * symbolConfig.roundingUnit;
  
  console.log(`\n📊 CALCULATION:`);
  console.log(`   Qty: ${referenceQty}`);
  console.log(`   ${symbolConfig.symbolType} Multiplier: ${referenceMultiplier}`);
  console.log(`   Raw Base: ${referenceQty} ÷ ${referenceMultiplier} = ${rawBaseQty}`);
  console.log(`   Rounded Base: ${rawBaseQty} → ${baseQty} (nearest multiple of ${symbolConfig.roundingUnit})`);
  console.log(`🎉 FINAL BASE QUANTITY: ${baseQty}`);
  console.log(`=== END ${symbolConfig.symbolType} DEBUGGING ===\n`);
  
  return baseQty;
};

// Helper function to calculate expected quantity for any index symbol
const calculateIndexExpectedQuantity = (baseQty, targetMultiplier, roundingUnit) => {
  // Calculate expected quantity
  const expectedQty = baseQty * targetMultiplier;
  
  // Round to nearest multiple of symbol-specific rounding unit
  const roundedQty = Math.round(expectedQty / roundingUnit) * roundingUnit;
  
  console.log(`Index calculation: base=${baseQty} * multiplier=${targetMultiplier} = ${expectedQty} → rounded to ${roundedQty} (unit: ${roundingUnit})`);
  
  return roundedQty;
};

// Helper function to check if symbol is index option
const isIndexOption = (symbol) => {
  const upperSymbol = symbol?.toUpperCase();
  return upperSymbol?.includes('NIFTY') || upperSymbol?.includes('BANKNIFTY') || upperSymbol?.includes('FINNIFTY') || upperSymbol?.includes('SENSEX');
};

export default function NewOpenOrdersTable({
  openOrdersData,
  filteredOpenOrdersData = [],
  setFilteredOpenOrdersFno,
  setFilteredOpenOrdersEquity,
  sortOrder,
  setSortOrder,
  currentState,
  accountName = [], // This is the accountNames prop
  allUser = [], // Keeping this for backward compatibility
  ...props
}) {
  // Use accountName as the primary source, fallback to allUser for backward compatibility
  const accountNames = accountName.length > 0 ? accountName : allUser;
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [sideFilter, setSideFilter] = useState('ALL'); // New state for side filter

  console.log("AccountNames data:", accountNames);
  
  // Symbol detection with strategy-specific base quantities for all index symbols
  console.log("Symbol detection test:", filteredOpenOrdersData.map(order => ({
    symbol: order.symbol,
    strategy: order.strategy,
    side: order.side,
    normalizedSide: normalizeSide(order.side),
    isIndex: isIndexOption(order.symbol),
    qty: order.qty
  })));

  // Group orders by symbol + strategy + normalized side
  const groupedOrders = useMemo(() => {
    const groups = {};
    
    filteredOpenOrdersData.forEach(order => {
      const normalizedSide = normalizeSide(order.side);
      const key = `${order.symbol}-${order.strategy}-${normalizedSide}`;
      
      if (!groups[key]) {
        groups[key] = {
          symbol: order.symbol,
          strategy: order.strategy,
          side: order.side, // Keep original side for display
          normalizedSide: normalizedSide, // Add normalized side for processing
          orderDateTime: order.orderDateTime,
          limitPrice: order.limitPrice,
          stopPrice: order.stopPrice,
          status: order.status,
          actualQuantities: {}, // Store actual quantities from orders
          clientOrderMap: {} // Map client to their actual quantities
        };
      }
      
      // Store actual quantities and client mappings
      order.clientIds.forEach(clientId => {
        if (!groups[key].clientOrderMap[clientId]) {
          groups[key].clientOrderMap[clientId] = [];
        }
        groups[key].clientOrderMap[clientId].push(order.qty);
      });
      
      if (!groups[key].actualQuantities[order.qty]) {
        groups[key].actualQuantities[order.qty] = [];
      }
      groups[key].actualQuantities[order.qty].push(...order.clientIds);
    });
    
    // Remove duplicates from clientOrderMap
    Object.values(groups).forEach(group => {
      Object.keys(group.clientOrderMap).forEach(clientId => {
        group.clientOrderMap[clientId] = [...new Set(group.clientOrderMap[clientId])];
      });
    });
    
    return Object.values(groups);
  }, [filteredOpenOrdersData]);

  // Generate multiplier tiers - Strategy-specific base quantity for all index symbols
  const enhancedGroupedOrders = useMemo(() => {
    return groupedOrders.map(group => {
      const isIndex = isIndexOption(group.symbol);
      
      if (isIndex) {
        const symbolConfig = getSymbolConfig(group.symbol);
        
        if (!symbolConfig) {
          console.log(`❌ Unknown index symbol: ${group.symbol}`);
          return {
            ...group,
            multiplierTiers: [],
            isIndexOption: true,
            error: `Unknown index symbol: ${group.symbol}`
          };
        }
        
        // Use unified base quantity calculation for all index symbols with normalized side
        const baseQty = getIndexBaseQuantity(group.symbol, group.strategy, group.side, filteredOpenOrdersData, accountNames);
        
        if (!baseQty) {
          console.log(`❌ Could not determine base quantity for ${group.symbol} + ${group.strategy} + ${group.normalizedSide} - this should not happen with fallback!`);
          return {
            ...group,
            multiplierTiers: [],
            isIndexOption: true,
            error: `Failed to get base quantity for ${group.symbol} + ${group.strategy} + ${group.normalizedSide}`
          };
        }
        
        console.log(`Processing ${symbolConfig.symbolType} order ${group.symbol} (${group.strategy}, side ${group.normalizedSide}) with base quantity: ${baseQty} ${baseQty === symbolConfig.fallbackBaseQty ? '(FALLBACK)' : '(CA00246)'}`);
        
        // Get unique multipliers from accountNames for this specific symbol type
        const uniqueMultipliers = [...new Set(
          accountNames
            .map(user => user[symbolConfig.multiplierField])
            .filter(multiplier => multiplier && multiplier > 0)
        )].sort((a, b) => a - b);
        
        if (uniqueMultipliers.length === 0) {
          console.log(`❌ No valid multipliers found for ${symbolConfig.symbolType}`);
          return {
            ...group,
            multiplierTiers: [],
            isIndexOption: true,
            error: `No valid ${symbolConfig.symbolType} multipliers found`
          };
        }
        
        const multiplierTiers = uniqueMultipliers.map(multiplier => {
          // Calculate expected quantity using symbol-specific logic
          const expectedQty = calculateIndexExpectedQuantity(baseQty, multiplier, symbolConfig.roundingUnit);
          
          // Get clients who actually have this quantity (or closest to it)
          const clientsWithThisQty = group.actualQuantities[expectedQty] || [];
          
          // Also check for clients with quantities close to expected (within 10% tolerance for rounding)
          const tolerance = expectedQty * 0.1;
          const clientsWithSimilarQty = [];
          
          Object.entries(group.actualQuantities).forEach(([qty, clientIds]) => {
            const actualQty = parseInt(qty);
            if (Math.abs(actualQty - expectedQty) <= tolerance && actualQty !== expectedQty) {
              clientsWithSimilarQty.push(...clientIds);
            }
          });
          
          const allClientsWithOrder = [...new Set([...clientsWithThisQty, ...clientsWithSimilarQty])];
          
          return {
            multiplier,
            quantity: expectedQty,
            expectedQuantity: expectedQty,
            allClients: accountNames.filter(user => user[symbolConfig.multiplierField] === multiplier),
            clientsWithOrder: allClientsWithOrder
          };
        });
        
        return {
          ...group,
          multiplierTiers,
          isIndexOption: true,
          baseQuantity: baseQty,
          symbolConfig: symbolConfig
        };
      } else {
        // For stock options, keep current logic (unchanged)
        const quantityTiers = Object.entries(group.actualQuantities)
          .map(([qty, clientIds]) => ({
            quantity: parseInt(qty),
            clientIds: [...new Set(clientIds)]
          }))
          .sort((a, b) => a.quantity - b.quantity);
          
        return {
          ...group,
          quantityTiers,
          isIndexOption: false
        };
      }
    });
  }, [groupedOrders, accountNames, filteredOpenOrdersData]);

  console.log("Enhanced Grouped Orders (Dynamic multipliers for all index symbols):", enhancedGroupedOrders);

  // Search, filtering, and sorting logic
  const searchedAndSortedOrders = useMemo(() => {
    let result = [...enhancedGroupedOrders];
    
    // Apply side filter
    if (sideFilter !== 'ALL') {
      result = result.filter(order => normalizeSide(order.side) === sideFilter);
    }
    
    // Apply sorting first if configured
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];
        
        // Handle different data types
        if (sortConfig.key === 'symbol') {
          aValue = aValue?.replace("NSE:", "").toLowerCase();
          bValue = bValue?.replace("NSE:", "").toLowerCase();
        } else if (sortConfig.key === 'side') {
          aValue = normalizeSide(aValue);
          bValue = normalizeSide(bValue);
        } else if (sortConfig.key === 'netQty' || sortConfig.key === 'netAvg' || sortConfig.key === 'ltp' || sortConfig.key === 'pl') {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        }
        
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    // Then apply search reordering (matching items go to top while maintaining sort order within each group)
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase().trim();
      
      // Separate matching and non-matching positions while preserving their sorted order
      const matchingPositions = result.filter(position => 
        position.symbol?.toLowerCase()?.includes(searchLower)
      );
      const nonMatchingPositions = result.filter(position => 
        !position.symbol?.toLowerCase()?.includes(searchLower)
      );
      console.log("MATCHING POSITIONs-----------",matchingPositions);
      // Combine: matching positions first (maintaining their sort order), then non-matching (maintaining their sort order)
      result = [...matchingPositions, ...nonMatchingPositions];
    }
    
    return result;
  }, [enhancedGroupedOrders, searchTerm, sortConfig, sideFilter]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  const handleSideFilterChange = (side) => {
    setSideFilter(side);
  };

  const getSideColor = (side) => {
    return normalizeSide(side) === 'BUY' ? "text-green-400" : "text-red-400";
  };

  const getStatusDisplay = (status) => {
    const statusMap = {
      "1": "CANCELLED",
      "Cancelled": "CANCELLED",
      "2": "TRADED", 
      "Traded": "TRADED",
      "4": "TRANSIT",
      "5": "REJECTED",
      "Rejected": "REJECTED", 
      "6": "PENDING",
      "Pending": "PENDING",
      "7": "EXPIRED"
    };
    return statusMap[status] || "STATUS UNKNOWN";
  };

  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronsUpDown className="w-4 h-4" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ChevronsUpDown className="w-4 h-4 text-blue-400" /> : 
      <ChevronsUpDown className="w-4 h-4 text-blue-400 rotate-180" />;
  };

  // Check if no data at all or no data after filtering
  const hasNoData = !filteredOpenOrdersData || filteredOpenOrdersData.length === 0;
  const hasNoFilteredData = searchedAndSortedOrders.length === 0;

  if (hasNoData) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400">
        <div className="text-lg font-medium">No Open Orders Found</div>
      </div>
    );
  }

  if (hasNoFilteredData && !hasNoData) {
    const getFilterMessage = () => {
      if (sideFilter === 'BUY') return 'No Open Buy Orders Found';
      if (sideFilter === 'SELL') return 'No Open Sell Orders Found';
      return 'No Open Orders Found';
    };

    return (
      <div className="w-full h-[calc(100vh-60px)] bg-zinc-900 flex flex-col">
        {/* Search Bar */}
        <div className="flex items-center justify-between bg-zinc-800 border-b border-zinc-700 p-4 shrink-0 ">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by symbol..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-10 py-1 bg-zinc-700 border border-zinc-600 rounded-lg text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {searchTerm && (
              <button
                onClick={clearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
       
          <div className="flex items-center gap-6">
            {/* Side Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-zinc-400">Filter:</span>
              <div className="flex bg-zinc-700 rounded-lg p-1">
                {['ALL', 'BUY', 'SELL'].map((side) => (
                  <button
                    key={side}
                    onClick={() => handleSideFilterChange(side)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                      sideFilter === side
                        ? side === 'BUY' 
                          ? 'bg-emerald-600 text-white' 
                          : side === 'SELL' 
                          ? 'bg-rose-600 text-white'
                          : 'bg-blue-600 text-white'
                        : 'text-zinc-300 hover:text-white hover:bg-zinc-600'
                    }`}
                  >
                    {side}
                  </button>
                ))}
              </div>
            </div>

            {/* Total Count */}
            <div className="text-lg font-medium text-white">
              Total Open Orders: <span className="text-lg font-semibold text-green-500">{searchedAndSortedOrders.length}</span>
            </div>
          </div>
        </div>

        {/* Empty State Message */}
        <div className="flex-1 flex flex-col items-center justify-center text-zinc-400">
          <div className="text-lg font-medium">{getFilterMessage()}</div>
        </div>
      </div>
    );
  }

  console.log("Final Displayed Orders:", searchedAndSortedOrders);

  return (
    <div className="w-full h-[calc(100vh-60px)] bg-zinc-900 flex flex-col">
      {/* Search Bar */}
      <div className="flex items-center justify-between bg-zinc-800 border-b border-zinc-700 p-4 shrink-0 ">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search by symbol..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-10 py-1 bg-zinc-700 border border-zinc-600 rounded-lg text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
     
        <div className="flex items-center gap-6">
          {/* Side Filter */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-zinc-400">Filter:</span>
            <div className="flex bg-zinc-700 rounded-lg p-1">
              {['ALL', 'BUY', 'SELL'].map((side) => (
                <button
                  key={side}
                  onClick={() => handleSideFilterChange(side)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    sideFilter === side
                      ? side === 'BUY' 
                        ? 'bg-green-600 text-white' 
                        : side === 'SELL' 
                        ? 'bg-red-600 text-white'
                        : 'bg-blue-600 text-white'
                      : 'text-zinc-300 hover:text-white hover:bg-zinc-600'
                  }`}
                >
                  {side}
                </button>
              ))}
            </div>
          </div>

          {/* Total Count */}
          <div className="text-lg font-medium text-white">
            Total Open Orders: <span className="text-lg font-semibold text-green-500">{searchedAndSortedOrders.length}</span>
          </div>
        </div>
      </div>

      {/* Sticky Header */}
      <div className="bg-zinc-800 border-b border-zinc-700 sticky top-0 z-10 shrink-0">
        <div className="grid grid-cols-6 gap-4 px-6 py-2 text-sm font-medium text-zinc-300">
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('symbol')}
          >
            <span>Symbol</span>
            {getSortIcon('symbol')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('orderDateTime')}
          >
            <span>Date</span>
            {getSortIcon('orderDateTime')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-left hover:text-white transition-colors"
            onClick={() => handleSort('side')}
          >
            <span>Side</span>
            {getSortIcon('side')}
          </button>

          <button 
            className="flex items-center space-x-2 text-center hover:text-white transition-colors"
            onClick={() => handleSort('limitPrice')}
          >
            <span>Limit Price</span>
            {getSortIcon('limitPrice')}
          </button>

          <button 
            className="flex items-center space-x-2 text-center hover:text-white transition-colors"
            onClick={() => handleSort('stopPrice')}
          >
            <span>Stop Price</span>
            {getSortIcon('stopPrice')}
          </button>
          
          <button 
            className="flex items-center space-x-2 text-center hover:text-white transition-colors"
            onClick={() => handleSort('strategy')}
          >
            <span>Strategy</span>
            {getSortIcon('strategy')}
          </button>
        </div>
      </div>

      {/* Scrollable Table Body - No Pagination, All Results Shown */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="min-w-full bg-zinc-900">
          <div className="divide-y divide-zinc-700">
            {searchedAndSortedOrders.map((order, index) => (
              console.log("ORDERS_--------------",order),
              <div key={index} className={` ${
                searchTerm && order?.symbol?.toLowerCase()?.includes(searchTerm.toLowerCase()) 
                  ? ' bg-[#07F2C7]/50' 
                  : 'bg-zinc-900'
              }`}>
                {/* Main Order Row */}
                <div className="grid grid-cols-6 gap-4 px-6 py-2 text-sm text-zinc-200 hover:bg-zinc-800 transition-colors">
                  <div className="font-medium">
                    {order.symbol?.replace("NSE:", "")}
                  </div>
                  <div>{FormatDateTime(order.orderDateTime)}</div>
                  <div className={`font-medium ${getSideColor(order.side)}`}>
                    {normalizeSide(order.side)}
                  </div>
                  <div className="text-center pr-20">₹{order.limitPrice}</div>
                  <div className="text-center pr-20">₹{order.stopPrice}</div>
                  <div className="font-medium text-center pr-20">{order.strategy}</div>
                </div>

                {/* Enhanced Quantity-Client Sub Table */}
                <div className="px-6 pb-4">
                  <div className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700">
                    {order.isIndexOption ? (
                      // Index Options - Dynamic multipliers for all index symbols
                      <>
                        <div className="bg-zinc-700 px-5 py-2">
                          <div className="flex gap-4 text-xs font-medium text-zinc-300">
                            <div className="min-w-[125px] flex items-start justify-start">
                              {order.symbolConfig ? `${order.symbolConfig.symbolType} Multiplier` : 'Index Multiplier'}
                            </div>
                            <div className="min-w-[125px] flex items-start justify-start">Expected Qty</div>
                            <div className="min-w-[140px] flex items-start justify-start">Clients</div>
                            {order.baseQuantity && order.symbolConfig && (
                              <div className="min-w-[180px] flex items-start justify-start text-amber-400">
                                {`Base: ${order.baseQuantity} (${order.baseQuantity === order.symbolConfig.fallbackBaseQty ? 'Fallback' : 'CA00246'} + Strategy, Unit: ${order.symbolConfig.roundingUnit})`}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="divide-y divide-zinc-700">
                          {order.multiplierTiers && order.multiplierTiers.length > 0 ? (
                            order.multiplierTiers.map((tier, tierIndex) => (
                              <div key={tierIndex} className="grid grid-cols-[120px,120px,1fr] gap-4 px-4 py-3 text-sm items-start">
                                <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
                                  {tier.multiplier}
                                </div>
                                <div className="font-medium text-emerald-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
                                  {tier.quantity}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  {tier.allClients.map((user, userIndex) => {
                                    // const hasOrder = tier.clientsWithOrder.includes(user.client_id);
                                    const hasOrder = order.clientOrderMap[user.client_id] && order.clientOrderMap[user.client_id].length > 0;
                                      
                                    // // Get actual quantities for this client
                                    const actualQuantities = order.clientOrderMap?.[user.client_id] || [];
                                    
                                    // // Check if any actual quantity matches the tier quantity
                                    const quantityMatches = actualQuantities.some(qty => qty === tier.quantity);
                                    
                                    // // Determine background color based on three conditions:
                                    // // 1. No position: bg-zinc-700 (gray)
                                    // // 2. Has position + quantity matches: bg-green-800/bg-rose-900 (green/red based on side)
                                    // // 3. Has position + quantity mismatch: bg-blue-500 (blue)
                                    let bgColor = 'bg-zinc-700'; // Default: no position
                                    
                                    if (hasOrder) {
                                      if (quantityMatches) {
                                        // Position exists and quantity matches tier quantity
                                        bgColor = order.side === 1 
                                          ? 'bg-green-800 border border-green-500/30' 
                                          : 'bg-rose-900 border border-rose-500/30';
                                      } else {
                                        // Position exists but quantity doesn't match tier quantity
                                        bgColor = 'bg-amber-700 border border-amber-500/30';
                                      }
                                    }
                                    
                                    // Override if tier quantity is negative
                                    if (tier.quantity < 0) {
                                      bgColor = 'bg-rose-900 border border-rose-500/30';
                                    }
                                    return (
                                      <span 
                                        key={userIndex}
                                        className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${
                                         bgColor
                                        }`}
                                      >
                                        {hasOrder && (
                                          <Check className="w-3 h-3 text-white" />
                                        )}
                                        <span>{user.name}</span>
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-sm text-red-400">
                              {order.error || 'No multiplier tiers available'}
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      // Stock Options - Current Logic (unchanged)
                      <>
                        <div className="bg-zinc-700 px-4 py-2">
                          <div className="grid grid-cols-2 gap-4 text-xs font-medium text-zinc-300">
                            <div>Quantity</div>
                            <div>Clients</div>
                          </div>
                        </div>
                        
                        <div className="divide-y divide-zinc-700">
                          {order.quantityTiers.map((tier, tierIndex) => (
                            <div key={tierIndex} className="grid grid-cols-[120px,1fr] gap-4 px-4 py-3 text-sm items-start">
                              <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
                                {tier.quantity}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {accountNames
                                  .filter(user => tier.clientIds?.includes(user.client_id))
                                  .map((user, userIndex) => {
                                    const hasOrder = tier.clientIds?.includes(user.client_id);
                                    return (
                                      <span 
                                        key={userIndex}
                                        className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${
                                          hasOrder && order.side === 1 ? 'bg-green-900 border border-green-500/30' : 
                                          hasOrder  && order.side === -1 ? 'bg-rose-900 border border-rose-500/30' : 'bg-zinc-700'
                                        }`}
                                      >
                                        {hasOrder && (
                                          <Check className="w-3 h-3 text-white" />
                                        )}
                                        <span>{user.name}</span>
                                      </span>
                                    );
                                  })
                                }
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}











































































// import React, { useState, useMemo } from "react";
// import { ChevronsUpDown, ChevronLeft, ChevronRight, Check, Search, X } from "lucide-react";

// // Mock FormatDateTime function
// const FormatDateTime = (dateString) => {
//   return dateString;
// };

// // Helper function to normalize side values
// const normalizeSide = (side) => {
//   // Convert to number if it's a string
//   const numSide = typeof side === 'string' ? parseInt(side) : side;
  
//   // Normalize: 1 = BUY, anything else (-1, 2, etc.) = SELL
//   return numSide === 1 ? 'BUY' : 'SELL';
// };

// // Helper function to get symbol-specific multiplier and rounding unit
// const getSymbolConfig = (symbol) => {
//   const upperSymbol = symbol.toUpperCase();
  
//   if (upperSymbol.includes('BANKNIFTY')) {
//     return {
//       multiplierField: 'banknifty_multiplier',
//       roundingUnit: 35,
//       symbolType: 'BANKNIFTY',
//       fallbackBaseQty: 35 // Base quantity for BANKNIFTY (gets multiplied by user's multiplier)
//     };
//   } else if (upperSymbol.includes('FINNIFTY')) {
//     return {
//       multiplierField: 'finnifty_multiplier',
//       roundingUnit: 65,
//       symbolType: 'FINNIFTY',
//       fallbackBaseQty: 65 // Base quantity for FINNIFTY
//     };
//   } else if (upperSymbol.includes('MIDCPNIFTY')) {
//     return {
//       multiplierField: 'midcpnifty_multiplier',
//       roundingUnit: 20,
//       symbolType: 'MIDCPNIFTY',
//       fallbackBaseQty: 20 // Base quantity for MIDCPNIFTY
//     };
//   } else if (upperSymbol.includes('SENSEX')) {
//     return {
//       multiplierField: 'sensex_multiplier',
//       roundingUnit: 20,
//       symbolType: 'SENSEX',
//       fallbackBaseQty: 20 // Base quantity for SENSEX
//     };
//   } else if (upperSymbol.includes('NIFTY')) {
//     return {
//       multiplierField: 'index_multiplier',
//       roundingUnit: 75,
//       symbolType: 'NIFTY',
//       fallbackBaseQty: 75 // Base quantity for NIFTY
//     };
//   }
  
//   return null;
// };

// // Updated function to get base quantity for all index symbols using CA00246 reference
// const getIndexBaseQuantity = (symbol, strategy, side, orderData = [], accountNames = []) => {
//   const referenceClientId = "CA00246";
//   const symbolConfig = getSymbolConfig(symbol);
  
//   if (!symbolConfig) {
//     console.log(`❌ Symbol "${symbol}" is not recognized as an index symbol`);
//     return null;
//   }
  
//   // Normalize the target side for comparison
//   const normalizedTargetSide = normalizeSide(side);
  
//   console.log(`\n=== DEBUGGING ${symbolConfig.symbolType} BASE CALCULATION ===`);
//   console.log(`🎯 Target: Symbol="${symbol}", Strategy="${strategy}", Side=${side} (normalized: ${normalizedTargetSide}), Client="${referenceClientId}"`);
//   console.log(`📊 Symbol Config: Type=${symbolConfig.symbolType}, MultiplierField=${symbolConfig.multiplierField}, RoundingUnit=${symbolConfig.roundingUnit}`);
  
//   // Find reference client's multiplier from accountNames
//   console.log(`\n📋 Available Accounts:`);
//   accountNames.forEach(u => {
//     const multiplier = u[symbolConfig.multiplierField];
//     console.log(`  - ID: "${u.client_id}", Name: "${u.name}", ${symbolConfig.symbolType} Multiplier: ${multiplier}`);
//   });
  
//   const referenceClientData = accountNames.find(user => user.client_id === referenceClientId);
//   if (!referenceClientData) {
//     console.log(`❌ Reference client "${referenceClientId}" NOT FOUND in accountNames`);
//     console.log(`📋 Available client IDs: [${accountNames.map(u => `"${u.client_id}"`).join(', ')}]`);
    
//     // FALLBACK: Use symbol's default base quantity
//     console.log(`🔄 FALLING BACK TO DEFAULT BASE QUANTITY for ${symbolConfig.symbolType}`);
//     const fallbackBaseQty = symbolConfig.fallbackBaseQty;
//     console.log(`📊 Using fallback base quantity: ${fallbackBaseQty}`);
//     console.log(`🎉 FINAL BASE QUANTITY (FALLBACK): ${fallbackBaseQty}`);
//     console.log(`=== END ${symbolConfig.symbolType} DEBUGGING (FALLBACK) ===\n`);
    
//     return fallbackBaseQty;
//   }
  
//   const referenceMultiplier = referenceClientData[symbolConfig.multiplierField];
//   if (!referenceMultiplier || referenceMultiplier <= 0) {
//     console.log(`❌ Reference client "${referenceClientId}" has invalid ${symbolConfig.symbolType} multiplier: ${referenceMultiplier}`);
    
//     // FALLBACK: Use symbol's default base quantity
//     console.log(`🔄 FALLING BACK TO DEFAULT BASE QUANTITY for ${symbolConfig.symbolType}`);
//     const fallbackBaseQty = symbolConfig.fallbackBaseQty;
//     console.log(`📊 Using fallback base quantity: ${fallbackBaseQty}`);
//     console.log(`🎉 FINAL BASE QUANTITY (FALLBACK): ${fallbackBaseQty}`);
//     console.log(`=== END ${symbolConfig.symbolType} DEBUGGING (FALLBACK) ===\n`);
    
//     return fallbackBaseQty;
//   }
  
//   console.log(`✅ Found reference client: ID="${referenceClientData.client_id}", ${symbolConfig.symbolType} Multiplier=${referenceMultiplier}`);
  
//   // Show ALL orders for this symbol
//   console.log(`\n📦 All orders for symbol "${symbol}":`);
//   const symbolOrders = orderData.filter(o => o.symbol === symbol);
//   symbolOrders.forEach((o, i) => {
//     console.log(`  [${i}] Strategy="${o.strategy}", Side=${o.side} (normalized: ${normalizeSide(o.side)}), Qty=${o.qty}, ClientIDs=[${o.clientIds.join(', ')}]`);
//   });
  
//   // Find reference client's order quantity for this EXACT symbol + strategy + normalized side combination
//   console.log(`\n🔍 Looking for EXACT match: Symbol="${symbol}" + Strategy="${strategy}" + Side=${normalizedTargetSide} (any raw side value) + Client="${referenceClientId}"`);
  
//   const referenceOrder = orderData.find(order => 
//     order.symbol === symbol && 
//     order.strategy === strategy && 
//     normalizeSide(order.side) === normalizedTargetSide && // Use normalized side comparison
//     order.clientIds.includes(referenceClientId)
//   );
  
//   if (!referenceOrder) {
//     console.log(`❌ NO EXACT MATCH FOUND!`);
//     console.log(`📦 Orders matching symbol+strategy+normalized side (without client filter):`);
//     const partialMatches = orderData.filter(o => 
//       o.symbol === symbol && o.strategy === strategy && normalizeSide(o.side) === normalizedTargetSide
//     );
//     partialMatches.forEach(o => {
//       console.log(`  - Qty=${o.qty}, Side=${o.side} (normalized: ${normalizeSide(o.side)}), ClientIDs=[${o.clientIds.join(', ')}], Contains "${referenceClientId}"? ${o.clientIds.includes(referenceClientId)}`);
//     });
    
//     // FALLBACK: Use symbol's default base quantity
//     console.log(`🔄 FALLING BACK TO DEFAULT BASE QUANTITY for ${symbolConfig.symbolType}`);
//     const fallbackBaseQty = symbolConfig.fallbackBaseQty;
//     console.log(`📊 Using fallback base quantity: ${fallbackBaseQty}`);
//     console.log(`🎉 FINAL BASE QUANTITY (FALLBACK): ${fallbackBaseQty}`);
//     console.log(`=== END ${symbolConfig.symbolType} DEBUGGING (FALLBACK) ===\n`);
    
//     return fallbackBaseQty;
//   }
  
//   console.log(`✅ FOUND EXACT MATCH!`);
//   console.log(`📦 Order details: Strategy="${referenceOrder.strategy}", Side=${referenceOrder.side} (normalized: ${normalizeSide(referenceOrder.side)}), Qty=${referenceOrder.qty}`);
//   console.log(`👥 ClientIDs in this order: [${referenceOrder.clientIds.join(', ')}]`);
  
//   const referenceQty = referenceOrder.qty;
  
//   // Calculate base quantity using the reference client's actual multiplier
//   const rawBaseQty = referenceQty / referenceMultiplier;
  
//   // Round the base quantity to nearest multiple of symbol-specific rounding unit
//   const baseQty = Math.round(rawBaseQty / symbolConfig.roundingUnit) * symbolConfig.roundingUnit;
  
//   console.log(`\n📊 CALCULATION:`);
//   console.log(`   Qty: ${referenceQty}`);
//   console.log(`   ${symbolConfig.symbolType} Multiplier: ${referenceMultiplier}`);
//   console.log(`   Raw Base: ${referenceQty} ÷ ${referenceMultiplier} = ${rawBaseQty}`);
//   console.log(`   Rounded Base: ${rawBaseQty} → ${baseQty} (nearest multiple of ${symbolConfig.roundingUnit})`);
//   console.log(`🎉 FINAL BASE QUANTITY: ${baseQty}`);
//   console.log(`=== END ${symbolConfig.symbolType} DEBUGGING ===\n`);
  
//   return baseQty;
// };

// // Helper function to calculate expected quantity for any index symbol
// const calculateIndexExpectedQuantity = (baseQty, targetMultiplier, roundingUnit) => {
//   // Calculate expected quantity
//   const expectedQty = baseQty * targetMultiplier;
  
//   // Round to nearest multiple of symbol-specific rounding unit
//   const roundedQty = Math.round(expectedQty / roundingUnit) * roundingUnit;
  
//   console.log(`Index calculation: base=${baseQty} * multiplier=${targetMultiplier} = ${expectedQty} → rounded to ${roundedQty} (unit: ${roundingUnit})`);
  
//   return roundedQty;
// };

// // Helper function to check if symbol is index option
// const isIndexOption = (symbol) => {
//   const upperSymbol = symbol.toUpperCase();
//   return upperSymbol.includes('NIFTY') || upperSymbol.includes('BANKNIFTY') || upperSymbol.includes('FINNIFTY') || upperSymbol.includes('SENSEX');
// };

// export default function NewOpenOrdersTable({
//   openOrdersData,
//   filteredOpenOrdersData = [],
//   setFilteredOpenOrdersFno,
//   setFilteredOpenOrdersEquity,
//   sortOrder,
//   setSortOrder,
//   currentState,
//   accountName = [], // This is the accountNames prop
//   allUser = [], // Keeping this for backward compatibility
//   ...props
// }) {
//   // Use accountName as the primary source, fallback to allUser for backward compatibility
//   const accountNames = accountName.length > 0 ? accountName : allUser;
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
//   const [searchTerm, setSearchTerm] = useState('');

//   console.log("AccountNames data:", accountNames);
  
//   // Symbol detection with strategy-specific base quantities for all index symbols
//   console.log("Symbol detection test:", filteredOpenOrdersData.map(order => ({
//     symbol: order.symbol,
//     strategy: order.strategy,
//     side: order.side,
//     normalizedSide: normalizeSide(order.side),
//     isIndex: isIndexOption(order.symbol),
//     qty: order.qty
//   })));

//   // Group orders by symbol + strategy + normalized side
//   const groupedOrders = useMemo(() => {
//     const groups = {};
    
//     filteredOpenOrdersData.forEach(order => {
//       const normalizedSide = normalizeSide(order.side);
//       const key = `${order.symbol}-${order.strategy}-${normalizedSide}`;
      
//       if (!groups[key]) {
//         groups[key] = {
//           symbol: order.symbol,
//           strategy: order.strategy,
//           side: order.side, // Keep original side for display
//           normalizedSide: normalizedSide, // Add normalized side for processing
//           orderDateTime: order.orderDateTime,
//           limitPrice: order.limitPrice,
//           stopPrice: order.stopPrice,
//           status: order.status,
//           actualQuantities: {}, // Store actual quantities from orders
//           clientOrderMap: {} // Map client to their actual quantities
//         };
//       }
      
//       // Store actual quantities and client mappings
//       order.clientIds.forEach(clientId => {
//         if (!groups[key].clientOrderMap[clientId]) {
//           groups[key].clientOrderMap[clientId] = [];
//         }
//         groups[key].clientOrderMap[clientId].push(order.qty);
//       });
      
//       if (!groups[key].actualQuantities[order.qty]) {
//         groups[key].actualQuantities[order.qty] = [];
//       }
//       groups[key].actualQuantities[order.qty].push(...order.clientIds);
//     });
    
//     // Remove duplicates from clientOrderMap
//     Object.values(groups).forEach(group => {
//       Object.keys(group.clientOrderMap).forEach(clientId => {
//         group.clientOrderMap[clientId] = [...new Set(group.clientOrderMap[clientId])];
//       });
//     });
    
//     return Object.values(groups);
//   }, [filteredOpenOrdersData]);

//   // Generate multiplier tiers - Strategy-specific base quantity for all index symbols
//   const enhancedGroupedOrders = useMemo(() => {
//     return groupedOrders.map(group => {
//       const isIndex = isIndexOption(group.symbol);
      
//       if (isIndex) {
//         const symbolConfig = getSymbolConfig(group.symbol);
        
//         if (!symbolConfig) {
//           console.log(`❌ Unknown index symbol: ${group.symbol}`);
//           return {
//             ...group,
//             multiplierTiers: [],
//             isIndexOption: true,
//             error: `Unknown index symbol: ${group.symbol}`
//           };
//         }
        
//         // Use unified base quantity calculation for all index symbols with normalized side
//         const baseQty = getIndexBaseQuantity(group.symbol, group.strategy, group.side, filteredOpenOrdersData, accountNames);
        
//         if (!baseQty) {
//           console.log(`❌ Could not determine base quantity for ${group.symbol} + ${group.strategy} + ${group.normalizedSide} - this should not happen with fallback!`);
//           return {
//             ...group,
//             multiplierTiers: [],
//             isIndexOption: true,
//             error: `Failed to get base quantity for ${group.symbol} + ${group.strategy} + ${group.normalizedSide}`
//           };
//         }
        
//         console.log(`Processing ${symbolConfig.symbolType} order ${group.symbol} (${group.strategy}, side ${group.normalizedSide}) with base quantity: ${baseQty} ${baseQty === symbolConfig.fallbackBaseQty ? '(FALLBACK)' : '(CA00246)'}`);
        
//         // Get unique multipliers from accountNames for this specific symbol type
//         const uniqueMultipliers = [...new Set(
//           accountNames
//             .map(user => user[symbolConfig.multiplierField])
//             .filter(multiplier => multiplier && multiplier > 0)
//         )].sort((a, b) => a - b);
        
//         if (uniqueMultipliers.length === 0) {
//           console.log(`❌ No valid multipliers found for ${symbolConfig.symbolType}`);
//           return {
//             ...group,
//             multiplierTiers: [],
//             isIndexOption: true,
//             error: `No valid ${symbolConfig.symbolType} multipliers found`
//           };
//         }
        
//         const multiplierTiers = uniqueMultipliers.map(multiplier => {
//           // Calculate expected quantity using symbol-specific logic
//           const expectedQty = calculateIndexExpectedQuantity(baseQty, multiplier, symbolConfig.roundingUnit);
          
//           // Get clients who actually have this quantity (or closest to it)
//           const clientsWithThisQty = group.actualQuantities[expectedQty] || [];
          
//           // Also check for clients with quantities close to expected (within 10% tolerance for rounding)
//           const tolerance = expectedQty * 0.1;
//           const clientsWithSimilarQty = [];
          
//           Object.entries(group.actualQuantities).forEach(([qty, clientIds]) => {
//             const actualQty = parseInt(qty);
//             if (Math.abs(actualQty - expectedQty) <= tolerance && actualQty !== expectedQty) {
//               clientsWithSimilarQty.push(...clientIds);
//             }
//           });
          
//           const allClientsWithOrder = [...new Set([...clientsWithThisQty, ...clientsWithSimilarQty])];
          
//           return {
//             multiplier,
//             quantity: expectedQty,
//             expectedQuantity: expectedQty,
//             allClients: accountNames.filter(user => user[symbolConfig.multiplierField] === multiplier),
//             clientsWithOrder: allClientsWithOrder
//           };
//         });
        
//         return {
//           ...group,
//           multiplierTiers,
//           isIndexOption: true,
//           baseQuantity: baseQty,
//           symbolConfig: symbolConfig
//         };
//       } else {
//         // For stock options, keep current logic (unchanged)
//         const quantityTiers = Object.entries(group.actualQuantities)
//           .map(([qty, clientIds]) => ({
//             quantity: parseInt(qty),
//             clientIds: [...new Set(clientIds)]
//           }))
//           .sort((a, b) => a.quantity - b.quantity);
          
//         return {
//           ...group,
//           quantityTiers,
//           isIndexOption: false
//         };
//       }
//     });
//   }, [groupedOrders, accountNames, filteredOpenOrdersData]);

//   console.log("Enhanced Grouped Orders (Dynamic multipliers for all index symbols):", enhancedGroupedOrders);

//   // Search and sorting logic (Type 2 search - reorder, don't filter)
//   const searchedAndSortedOrders = useMemo(() => {
//         let result = [...enhancedGroupedOrders];
        
//         // Apply sorting first if configured
//         if (sortConfig.key) {
//           result.sort((a, b) => {
//             let aValue = a[sortConfig.key];
//             let bValue = b[sortConfig.key];
            
//             // Handle different data types
//             if (sortConfig.key === 'symbol') {
//               aValue = aValue.replace("NSE:", "").toLowerCase();
//               bValue = bValue.replace("NSE:", "").toLowerCase();
//             } else if (sortConfig.key === 'side') {
//               aValue = normalizeSide(aValue);
//               bValue = normalizeSide(bValue);
//             } else if (sortConfig.key === 'netQty' || sortConfig.key === 'netAvg' || sortConfig.key === 'ltp' || sortConfig.key === 'pl') {
//               aValue = parseFloat(aValue) || 0;
//               bValue = parseFloat(bValue) || 0;
//             }
            
//             if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
//             if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
//             return 0;
//           });
//         }
        
//         // Then apply search reordering (matching items go to top while maintaining sort order within each group)
//         if (searchTerm.trim()) {
//           const searchLower = searchTerm.toLowerCase().trim();
          
//           // Separate matching and non-matching positions while preserving their sorted order
//           const matchingPositions = result.filter(position => 
//             position.symbol.toLowerCase().includes(searchLower)
//           );
//           const nonMatchingPositions = result.filter(position => 
//             !position.symbol.toLowerCase().includes(searchLower)
//           );
          
//           // Combine: matching positions first (maintaining their sort order), then non-matching (maintaining their sort order)
//           result = [...matchingPositions, ...nonMatchingPositions];
//         }
        
//         return result;
//       }, [enhancedGroupedOrders, searchTerm, sortConfig]);

//   const handleSort = (key) => {
//     setSortConfig(prev => ({
//       key,
//       direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
//     }));
//   };

//   const handleSearchChange = (e) => {
//     setSearchTerm(e.target.value);
//   };

//   const clearSearch = () => {
//     setSearchTerm('');
//   };

//   const getSideColor = (side) => {
//     return normalizeSide(side) === 'BUY' ? "text-green-400" : "text-red-400";
//   };

//   const getStatusDisplay = (status) => {
//     const statusMap = {
//       "1": "CANCELLED",
//       "Cancelled": "CANCELLED",
//       "2": "TRADED", 
//       "Traded": "TRADED",
//       "4": "TRANSIT",
//       "5": "REJECTED",
//       "Rejected": "REJECTED", 
//       "6": "PENDING",
//       "Pending": "PENDING",
//       "7": "EXPIRED"
//     };
//     return statusMap[status] || "STATUS UNKNOWN";
//   };

//   const getSortIcon = (columnKey) => {
//     if (sortConfig.key !== columnKey) {
//       return <ChevronsUpDown className="w-4 h-4" />;
//     }
//     return sortConfig.direction === 'asc' ? 
//       <ChevronsUpDown className="w-4 h-4 text-blue-400" /> : 
//       <ChevronsUpDown className="w-4 h-4 text-blue-400 rotate-180" />;
//   };

//   if (!filteredOpenOrdersData || filteredOpenOrdersData.length === 0) {
//     return (
//       <div className="w-full h-full flex flex-col items-center justify-center text-zinc-400">
//         <div className="text-lg font-medium">No Open Orders Found</div>
//       </div>
//     );
//   }

//   return (
//     <div className="w-full h-[calc(100vh-60px)] bg-zinc-900 flex flex-col">
//       {/* Search Bar */}
//       <div className="flex items-center justify-between bg-zinc-800 border-b border-zinc-700 p-4 shrink-0 ">
//         <div className="relative max-w-md">
//           <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-zinc-400 w-4 h-4" />
//           <input
//             type="text"
//             placeholder="Search by symbol..."
//             value={searchTerm}
//             onChange={handleSearchChange}
//             className="w-full pl-10 pr-10 py-1 bg-zinc-700 border border-zinc-600 rounded-lg text-zinc-200 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
//           />
//           {searchTerm && (
//             <button
//               onClick={clearSearch}
//               className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-200 transition-colors"
//             >
//               <X className="w-4 h-4" />
//             </button>
//           )}
//         </div>
     
//         <div className=" text-lg font-medium text-white">
//           Total Open Orders: <span className="text-lg font-semibold text-green-500">{searchedAndSortedOrders.length}</span>
//         </div>
//       </div>

//       {/* Sticky Header */}
//       <div className="bg-zinc-800 border-b border-zinc-700 sticky top-0 z-10 shrink-0">
//         <div className="grid grid-cols-6 gap-4 px-6 py-2 text-sm font-medium text-zinc-300">
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('symbol')}
//           >
//             <span>Symbol</span>
//             {getSortIcon('symbol')}
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('orderDateTime')}
//           >
//             <span>Date</span>
//             {getSortIcon('orderDateTime')}
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-left hover:text-white transition-colors"
//             onClick={() => handleSort('side')}
//           >
//             <span>Side</span>
//             {getSortIcon('side')}
//           </button>

//           <button 
//             className="flex items-center space-x-2 text-center hover:text-white transition-colors"
//             onClick={() => handleSort('limitPrice')}
//           >
//             <span>Limit Price</span>
//             {getSortIcon('limitPrice')}
//           </button>

//           <button 
//             className="flex items-center space-x-2 text-center hover:text-white transition-colors"
//             onClick={() => handleSort('stopPrice')}
//           >
//             <span>Stop Price</span>
//             {getSortIcon('stopPrice')}
//           </button>
          
//           <button 
//             className="flex items-center space-x-2 text-center hover:text-white transition-colors"
//             onClick={() => handleSort('strategy')}
//           >
//             <span>Strategy</span>
//             {getSortIcon('strategy')}
//           </button>
//         </div>
//       </div>

//       {/* Scrollable Table Body - No Pagination, All Results Shown */}
//       <div className="flex-1 overflow-y-auto min-h-0">
//         <div className="min-w-full bg-zinc-900">
//           <div className="divide-y divide-zinc-700">
//             {searchedAndSortedOrders.map((order, index) => (
//               <div key={index} className={` ${
//                 searchTerm && order.symbol.toLowerCase().includes(searchTerm.toLowerCase()) 
//                   ? ' bg-[#07F2C7]/50' 
//                   : 'bg-zinc-900'
//               }`}>
//                 {/* Main Order Row */}
//                 <div className="grid grid-cols-6 gap-4 px-6 py-2 text-sm text-zinc-200 hover:bg-zinc-800 transition-colors">
//                   <div className="font-medium">
//                     {order.symbol.replace("NSE:", "")}
//                   </div>
//                   <div>{FormatDateTime(order.orderDateTime)}</div>
//                   <div className={`font-medium ${getSideColor(order.side)}`}>
//                     {normalizeSide(order.side)}
//                   </div>
//                   <div className="text-center pr-20">₹{order.limitPrice}</div>
//                   <div className="text-center pr-20">₹{order.stopPrice}</div>
//                   <div className="font-medium text-center pr-20">{order.strategy}</div>
//                 </div>

//                 {/* Enhanced Quantity-Client Sub Table */}
//                 <div className="px-6 pb-4">
//                   <div className="bg-zinc-800 rounded-lg overflow-hidden border border-zinc-700">
//                     {order.isIndexOption ? (
//                       // Index Options - Dynamic multipliers for all index symbols
//                       <>
//                         <div className="bg-zinc-700 px-5 py-2">
//                           <div className="flex gap-4 text-xs font-medium text-zinc-300">
//                             <div className="min-w-[125px] flex items-start justify-start">
//                               {order.symbolConfig ? `${order.symbolConfig.symbolType} Multiplier` : 'Index Multiplier'}
//                             </div>
//                             <div className="min-w-[125px] flex items-start justify-start">Expected Qty</div>
//                             <div className="min-w-[140px] flex items-start justify-start">Clients</div>
//                             {order.baseQuantity && order.symbolConfig && (
//                               <div className="min-w-[180px] flex items-start justify-start text-amber-400">
//                                 {`Base: ${order.baseQuantity} (${order.baseQuantity === order.symbolConfig.fallbackBaseQty ? 'Fallback' : 'CA00246'} + Strategy, Unit: ${order.symbolConfig.roundingUnit})`}
//                               </div>
//                             )}
//                           </div>
//                         </div>
                        
//                         <div className="divide-y divide-zinc-700">
//                           {order.multiplierTiers && order.multiplierTiers.length > 0 ? (
//                             order.multiplierTiers.map((tier, tierIndex) => (
//                               <div key={tierIndex} className="grid grid-cols-[120px,120px,1fr] gap-4 px-4 py-3 text-sm items-start">
//                                 <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
//                                   {tier.multiplier}
//                                 </div>
//                                 <div className="font-medium text-emerald-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
//                                   {tier.quantity}
//                                 </div>
//                                 <div className="flex flex-wrap gap-2">
//                                   {tier.allClients.map((user, userIndex) => {
//                                     const hasOrder = tier.clientsWithOrder.includes(user.client_id);
//                                     return (
//                                       <span 
//                                         key={userIndex}
//                                         className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${
//                                           hasOrder ? 'bg-green-800 border border-green-500/30' : 'bg-zinc-700'
//                                         }`}
//                                       >
//                                         {hasOrder && (
//                                           <Check className="w-3 h-3 text-white" />
//                                         )}
//                                         <span>{user.name}</span>
//                                       </span>
//                                     );
//                                   })}
//                                 </div>
//                               </div>
//                             ))
//                           ) : (
//                             <div className="px-4 py-3 text-sm text-red-400">
//                               {order.error || 'No multiplier tiers available'}
//                             </div>
//                           )}
//                         </div>
//                       </>
//                     ) : (
//                       // Stock Options - Current Logic (unchanged)
//                       <>
//                         <div className="bg-zinc-700 px-4 py-2">
//                           <div className="grid grid-cols-2 gap-4 text-xs font-medium text-zinc-300">
//                             <div>Quantity</div>
//                             <div>Clients</div>
//                           </div>
//                         </div>
                        
//                         <div className="divide-y divide-zinc-700">
//                           {order.quantityTiers.map((tier, tierIndex) => (
//                             <div key={tierIndex} className="grid grid-cols-[120px,1fr] gap-4 px-4 py-3 text-sm items-start">
//                               <div className="font-medium text-blue-400 bg-zinc-700 rounded px-3 py-1 text-center shrink-0">
//                                 {tier.quantity}
//                               </div>
//                               <div className="flex flex-wrap gap-2">
//                                 {accountNames
//                                   .filter(user => tier.clientIds.includes(user.client_id))
//                                   .map((user, userIndex) => {
//                                     const hasOrder = tier.clientIds.includes(user.client_id);
//                                     return (
//                                       <span 
//                                         key={userIndex}
//                                         className={`text-zinc-300 rounded px-2 py-1 text-xs flex items-center space-x-1 ${
//                                           hasOrder ? 'bg-green-900 border border-green-500/30' : 'bg-zinc-700'
//                                         }`}
//                                       >
//                                         {hasOrder && (
//                                           <Check className="w-3 h-3 text-white" />
//                                         )}
//                                         <span>{user.name}</span>
//                                       </span>
//                                     );
//                                   })
//                                 }
//                               </div>
//                             </div>
//                           ))}
//                         </div>
//                       </>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             ))}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }


















































































































